import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { MessageBoardThread } from '../../models/entities/message-board-thread';

/** Interface with various attributes of a thread that could be updated in a patch request */
export interface UpdateThreadRequest
{
    sortOrder?: number;
    isPinned?: boolean;
    hasCommentsEnabled?: boolean;
    title?: string;
    content?: string;
    hasFeaturedImage?: boolean;
    featuredImageBase64?: string;
    attachmentFileIds?: number[];
    doNotifyEveryone?: boolean;
    isAuthorSubscribed?: boolean;
    subscriptionFrequency?: string;
}

@Injectable()
export class MessageBoardThreadService
{
    constructor(
            private _http: ApplicationHttpClient,
    ) {
    }

    /** Get a thread with threads, subs, etc */
    public getThread(threadId: number): Observable<MessageBoardThread> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.MessageBoardThreadDetail(jwt.orgId, threadId));

        return this._http.get(url)
                .pipe(
                        map((thread: Object) => {
                            return new MessageBoardThread(thread);
                        }));
    }

    /** Create a thread */
    public createThread(boardId: number, request: UpdateThreadRequest): Observable<number> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.MessageBoardDetailAction(jwt.orgId, boardId, 'thread'));

        return this._http.post(url, JSON.stringify(request))
                .pipe(
                        map((threadId: number) => {
                            return threadId;
                        }));
    }

    /** Update a thread */
    public updateThread(threadId: number, request: UpdateThreadRequest) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.MessageBoardThreadDetail(jwt.orgId, threadId));

        return this._http.patch(url, JSON.stringify(request));
    }

    /** Update a threads sort or pinned status */
    public updateThreadSort(threadId: number, request: UpdateThreadRequest) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.MessageBoardThreadDetailAction(jwt.orgId, threadId, 'sort'));

        return this._http.patch(url, JSON.stringify(request));
    }

    /** Delete a thread */
    public deleteThread(threadId: number) {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.MessageBoardThreadDetail(jwt.orgId, threadId));

        return this._http.delete(url);
    }

}

